import type { FC, ReactNode } from 'react'
import type { ToolSummary } from '@/types'

import { cn } from '@/lib/utils'

interface BadgeProps {
    tone: 'free' | 'open' | 'dead'
    children: ReactNode
}

const tones = {
    free: 'bg-emerald-400/10 text-emerald-300 ring-emerald-400/25',
    open: 'bg-sky-400/10 text-sky-300 ring-sky-400/25',
    dead: 'bg-rose-400/10 text-rose-300 ring-rose-400/25'
}

export const Badge: FC<BadgeProps> = ({ tone, children }): ReactNode => {
    return (
        <span className={cn('inline-flex shrink-0 items-center rounded-full px-2 py-0.5 font-mono text-[11px] font-medium leading-4 ring-1 ring-inset', tones[tone])}>
            {children}
        </span>
    )
}

interface ToolBadgesProps {
    tool: Pick<ToolSummary, 'is_free' | 'is_open_source' | 'discontinued'>
}

export const ToolBadges: FC<ToolBadgesProps> = ({ tool }): ReactNode => {
    if (!tool.is_free && !tool.is_open_source && !tool.discontinued) return null

    return (
        <span className='inline-flex flex-wrap items-center gap-1.5'>
            {tool.discontinued && <Badge tone='dead'>dead</Badge>}
            {!tool.discontinued && tool.is_free && <Badge tone='free'>free</Badge>}
            {!tool.discontinued && tool.is_open_source && <Badge tone='open'>open source</Badge>}
        </span>
    )
}

interface ScoreBadgeProps {
    score: number
    size?: 'sm' | 'lg'
}

export const ScoreBadge: FC<ScoreBadgeProps> = ({ score, size = 'sm' }): ReactNode => {
    const top = score >= 9

    return (
        <span
            className={cn(
                'inline-flex shrink-0 items-center justify-center rounded-lg font-mono font-semibold tabular-nums ring-1 ring-inset',
                size === 'sm' ? 'min-w-11 px-2 py-1 text-sm' : 'min-w-16 px-3 py-2 text-2xl',
                top ? 'bg-primary/12 text-primary ring-primary/30' : 'bg-secondary text-foreground ring-border'
            )}
        >
            <span className='sr-only'>Score </span>
            {score.toFixed(1)}
            <span className='sr-only'> out of 10</span>
        </span>
    )
}
